import React from "react";
import BlockRenderer from "../components/canvas/BlockRenderer";
import { Badge } from "../components/index";
import type { InlineArtifactData } from "./InlineArtifact";

// ---- Types ----

interface ArtifactSlideOverProps {
  artifact: InlineArtifactData | null;
  open: boolean;
  onClose: () => void;
  onBlockUpdate?: (artifactId: string, blockId: string, payload: Record<string, unknown>) => void;
  onBlockApprove?: (artifactId: string, blockId: string, notes?: string) => void;
  onBlockReject?: (artifactId: string, blockId: string, notes?: string) => void;
  onStatusChange?: (artifactId: string, status: string) => void;
}

// ---- Status helpers ----

const STATUS_STYLES: Record<
  string,
  { variant: "blue" | "amber" | "green" | "default"; label: string }
> = {
  draft: { variant: "default", label: "Draft" },
  review: { variant: "amber", label: "Review" },
  approved: { variant: "green", label: "Approved" },
  archived: { variant: "default", label: "Archived" },
};

// ---- Component ----

export default function ArtifactSlideOver({
  artifact,
  open,
  onClose,
  onBlockUpdate,
  onBlockApprove,
  onBlockReject,
  onStatusChange,
}: ArtifactSlideOverProps): React.ReactElement | null {
  if (!open || !artifact) return null;

  const statusInfo = STATUS_STYLES[artifact.status] ?? STATUS_STYLES.draft;
  const isInteractive = artifact.status === "review";

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="relative ml-auto w-full max-w-2xl h-full flex flex-col overflow-hidden"
        style={{
          backgroundColor: "var(--c-bg)",
          borderLeft: "1px solid var(--c-border)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 shrink-0"
          style={{ borderBottom: "1px solid var(--c-border)", backgroundColor: "var(--c-surface)" }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <svg
              width="18"
              height="18"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              style={{ color: "var(--c-primary)" }}
              className="shrink-0"
            >
              <rect x="2" y="1.5" width="10" height="11" rx="1.5" />
              <path d="M5 4.5h4M5 7h4M5 9.5h2" />
            </svg>
            <div className="min-w-0">
              <h2 className="text-base font-semibold truncate" style={{ color: "var(--c-text)" }}>
                {artifact.title}
              </h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
                  {artifact.kind}
                </span>
                <Badge variant={statusInfo.variant} size="sm">
                  {statusInfo.label}
                </Badge>
                <span className="text-[10px]" style={{ color: "var(--c-text-faint)" }}>
                  {artifact.blocks.length} block{artifact.blocks.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Status transition buttons */}
            {onStatusChange && artifact.status === "draft" && (
              <button
                onClick={() => onStatusChange(artifact.artifactId, "review")}
                className="text-xs px-3 py-1.5 rounded-lg font-medium"
                style={{
                  backgroundColor: "var(--c-primary-subtle)",
                  color: "var(--c-primary)",
                  border: "1px solid var(--c-primary)",
                }}
              >
                Send to Review
              </button>
            )}
            {onStatusChange && artifact.status === "review" && (
              <button
                onClick={() => onStatusChange(artifact.artifactId, "approved")}
                className="text-xs px-3 py-1.5 rounded-lg font-medium"
                style={{
                  backgroundColor: "var(--c-success)",
                  color: "white",
                }}
              >
                Approve All
              </button>
            )}

            {/* Close button */}
            <button
              onClick={onClose}
              className="p-1.5 rounded-md transition-colors"
              style={{ color: "var(--c-text-secondary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--c-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </div>
        </div>

        {/* Block list */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {artifact.blocks.map((block) => (
            <BlockRenderer
              key={block.blockId}
              block={block}
              interactive={isInteractive}
              onBlockUpdate={
                onBlockUpdate
                  ? (blockId, payload) => onBlockUpdate(artifact.artifactId, blockId, payload)
                  : undefined
              }
              onBlockApprove={
                onBlockApprove
                  ? (blockId, notes) => onBlockApprove(artifact.artifactId, blockId, notes)
                  : undefined
              }
              onBlockReject={
                onBlockReject
                  ? (blockId, notes) => onBlockReject(artifact.artifactId, blockId, notes)
                  : undefined
              }
            />
          ))}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-6 py-3 shrink-0"
          style={{ borderTop: "1px solid var(--c-border)", backgroundColor: "var(--c-surface)" }}
        >
          <span className="text-[11px]" style={{ color: "var(--c-text-faint)" }}>
            Created {new Date(artifact.createdAt).toLocaleString()}
          </span>
          <span className="text-[11px] font-mono" style={{ color: "var(--c-text-faint)" }}>
            {artifact.artifactId}
          </span>
        </div>
      </div>
    </div>
  );
}
